/**
 * SMS Campaign Sent Contacts page — delivery status per recipient.
 * Route: /marketing/sms-campaigns/:id/sent-contacts
 *
 * Mirrors the EmailCampaignContactsPage pattern: full-height DataTable,
 * FilterTabs with status counts, search, formatted phone numbers,
 * and a contact details modal with delivery timeline and events.
 */
import { useState, useMemo, useCallback } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Search, Loader2 } from 'lucide-react';
import { DataTable } from '@/components/ui/DataTable';
import type { DataTableColumn } from '@/components/ui/DataTable';
import { Input } from '@/components/ui/Input';
import { FilterSelect } from '@/components/ui/FilterSelect';
import type { FilterSelectOption } from '@/components/ui/FilterSelect';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/useToast';
import * as marketingSvc from '@/services/marketing.service';
import type { SmsCampaignContact, SmsContactDetailResponse } from '@/services/marketing.service';
import { ContactLeadCell, ContactLeadFilter, ContactLeadList, ContactLeadOpener, leadFilterFromUrl, leadParam, useContactLeadModal } from '@/components/marketing/ContactLeads';
import { formatPhone } from '@/lib/utils';
import { useUserTimezone } from '@/hooks/useUserTimezone';
import { pacificToWallClock, getTimezoneAbbreviation } from '@/lib/facilityTimezone';

// Delivery timestamps arrive as Pacific wall-clock DATETIME strings
// (config('app.timezone')); reproject into the viewer's zone and suffix the
// abbreviation so queued/sent/delivered times are unambiguous.
function fmtDate(dateStr: string | null | undefined, tz: string): string {
  if (!dateStr) return '\u2014';
  const m = dateStr.match(/^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2})/);
  if (!m) return '\u2014';
  const wall = pacificToWallClock(m[1], m[2], tz);
  const [y, mo, d] = wall.date.split('-').map(Number);
  const [h, mi] = wall.time.split(':').map(Number);
  const dateObj = new Date(y, mo - 1, d, h, mi);
  if (isNaN(dateObj.getTime())) return '\u2014';
  const datePart = dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const timePart = dateObj.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  return `${datePart} ${timePart} ${getTimezoneAbbreviation(tz, dateObj)}`;
}

const STATUS_ICON: Record<string, { bg: string; color: string; svg: string }> = {
  queued:      { bg: '#fff3cd', color: '#856404', svg: '<circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>' },
  sending:     { bg: '#fff3cd', color: '#856404', svg: '<circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>' },
  sent:        { bg: '#cce5ff', color: '#004085', svg: '<line x1="22" x2="11" y1="2" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>' },
  delivered:   { bg: '#d4edda', color: '#155724', svg: '<polyline points="20 6 9 17 4 12"/>' },
  undelivered: { bg: '#fff3cd', color: '#856404', svg: '<circle cx="12" cy="12" r="10"/><line x1="12" x2="12" y1="8" y2="12"/><line x1="12" x2="12.01" y1="16" y2="16"/>' },
  failed:      { bg: '#f8d7da', color: '#721c24', svg: '<circle cx="12" cy="12" r="10"/><line x1="15" x2="9" y1="9" y2="15"/><line x1="9" x2="15" y1="9" y2="15"/>' },
};

export default function SmsCampaignSentContactsPage() {
  const { id } = useParams<{ id: string }>();
  const campaignId = Number(id);
  const toast = useToast();
  const { hasMinUserLevel } = useAuth();
  // The failure-triage options split Twilio failures into "our fault /
  // retryable" vs "bad number" buckets — internal diagnostics, level 5 only.
  const canSeeFailureTriage = hasMinUserLevel(5);
  const userTz = useUserTimezone();

  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  // Seeded from the link a stats-page box opened (?filter=, ?lead=).
  const [searchParams] = useSearchParams();
  const [contactFilter, setContactFilter] = useState(() => searchParams.get('filter') ?? '');
  const [leadFilter, setLeadFilter] = useState(() => leadFilterFromUrl(searchParams.get('lead')));
  const perPage = 100;

  const [modalContact, setModalContact] = useState<SmsCampaignContact | null>(null);
  const [detailData, setDetailData] = useState<SmsContactDetailResponse | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const { data: campaign } = useQuery({
    queryKey: ['marketing', 'sms-campaigns', campaignId],
    queryFn: () => marketingSvc.getSmsCampaign(campaignId),
    enabled: !!id,
  });

  const { data: contactsData, isLoading } = useQuery({
    queryKey: ['sms-campaign-sent-contacts', id, search, page, contactFilter, leadFilter],
    queryFn: () => marketingSvc.getSmsCampaignContactsPaginated(campaignId, {
      search: search || undefined,
      page,
      perPage,
      contactFilter: contactFilter || undefined,
      lead: leadParam(leadFilter),
    }),
    enabled: !!id,
  });

  const contacts = contactsData?.data ?? [];
  const paginationTotal = contactsData?.meta?.total ?? 0;
  const statusCounts = contactsData?.statusCounts;

  // Status filter options — dot colour / text colour mirror the row status
  // badges below. The two failure-triage buckets are level 5 only.
  const FILTER_OPTIONS = useMemo<FilterSelectOption[]>(() => {
    const sc = statusCounts;
    return [
      { label: 'All Contacts', value: '', count: campaign?.totalRecipients, color: '#3b82f6', textColor: '#1d4ed8' },
      // What the stats page's "SMS Sent" counts: a delivered text was also sent.
      { label: 'Sent or delivered', value: 'sent_any', count: sc ? sc.sent + sc.delivered : undefined, color: '#2e7d32', textColor: '#15803d' },
      { label: 'Delivered', value: 'delivered', count: sc?.delivered, color: '#22c55e', textColor: '#15803d' },
      { label: 'Sent', value: 'sent', count: sc?.sent, color: '#0ea5e9', textColor: '#0369a1' },
      { label: 'Queued', value: 'queued', count: sc?.queued, color: '#f59e0b', textColor: '#b45309' },
      { label: 'Sending', value: 'sending', count: sc?.sending, color: '#8b5cf6', textColor: '#6d28d9' },
      { label: 'Undelivered', value: 'undelivered', count: sc?.undelivered, color: '#fbbf24', textColor: '#a16207' },
      { label: 'Failed', value: 'failed', count: sc?.failed, color: '#ef4444', textColor: '#b91c1c' },
      ...(canSeeFailureTriage ? [
        { label: 'Failed (System)', value: 'failed_system', count: sc?.failed_system, color: '#fb7185', textColor: '#be123c' },
        { label: 'Failed (Invalid)', value: 'failed_permanent', count: sc?.failed_permanent, color: '#dc2626', textColor: '#991b1b' },
      ] : []),
      { label: 'Unsubscribed', value: 'unsubscribed', count: sc?.unsubscribed, color: '#f97316', textColor: '#c2410c' },
    ];
  }, [statusCounts, campaign?.totalRecipients, canSeeFailureTriage]);

  const openContactDetail = useCallback(async (row: SmsCampaignContact) => {
    setModalContact(row);
    setDetailData(null);
    setDetailLoading(true);
    try {
      const data = await marketingSvc.getSmsCampaignContactDetail(campaignId, row.smsCampaignContactID);
      setDetailData(data);
    } catch {
      toast.error('Failed to load contact details');
    } finally {
      setDetailLoading(false);
    }
  }, [campaignId, toast]);

  const closeModal = useCallback(() => {
    setModalContact(null);
    setDetailData(null);
  }, []);

  // The lead modal opens over the list; its Spam / Trash / Convert can change
  // what the Lead column and the open contact show, so both reload after it.
  const queryClient = useQueryClient();
  const openContactId = modalContact?.smsCampaignContactID ?? null;
  const leadModal = useContactLeadModal(useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['sms-campaign-sent-contacts', id] });
    if (openContactId != null) {
      marketingSvc.getSmsCampaignContactDetail(campaignId, openContactId).then(setDetailData).catch(() => undefined);
    }
  }, [queryClient, id, campaignId, openContactId]));

  const columns: DataTableColumn<SmsCampaignContact>[] = useMemo(() => [
    {
      key: 'phoneNumber',
      header: 'Phone Number',
      sortable: true,
      render: (row) => <span>{formatPhone(row.phoneNumber)}</span>,
    },
    {
      key: 'firstName',
      header: 'First Name',
      sortable: true,
      render: (row) => <span>{row.pFirstName || row.firstName || ''}</span>,
    },
    {
      key: 'lastName',
      header: 'Last Name',
      sortable: true,
      render: (row) => <span>{row.pLastName || row.lastName || ''}</span>,
    },
    {
      key: 'deliveryStatus',
      header: 'Status',
      sortable: true,
      render: (row) => {
        const s = row.deliveryStatus || 'queued';
        const cls: Record<string, string> = {
          delivered: 'sms-status-delivered',
          sent: 'sms-status-sent',
          queued: 'sms-status-queued',
          sending: 'sms-status-queued',
          undelivered: 'sms-status-undelivered',
          failed: 'sms-status-failed',
          unsubscribed: 'sms-status-unsubscribed',
        };
        return (
          <span className={`sms-status-badge ${cls[s] || 'sms-status-default'}`}>
            {s.charAt(0).toUpperCase() + s.slice(1)}
          </span>
        );
      },
    },
    {
      key: 'errorMessage',
      header: 'Error',
      render: (row) => (
        <span style={{ fontSize: 12, color: row.errorMessage ? '#dc3545' : '#999', maxWidth: 250, display: 'inline-block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={row.errorMessage || ''}>
          {row.errorMessage || '\u2014'}
        </span>
      ),
    },
    {
      key: 'leads',
      header: 'Lead',
      render: (row) => <ContactLeadCell leads={row.leads} />,
    },
    {
      key: 'sentAt',
      header: 'Sent At',
      sortable: true,
      render: (row) => fmtDate(row.sentAt, userTz),
    },
    {
      key: 'createDate',
      header: 'Queued At',
      sortable: true,
      render: (row) => fmtDate(row.createDate, userTz),
    },
  ], [userTz]);

  const totalRecipients = campaign?.totalRecipients ?? paginationTotal;

  return (
    <ContactLeadOpener value={leadModal.openLead}>
      <style>{`
        .sms-status-badge {
          display: inline-block; padding: 3px 10px; border-radius: 12px;
          font-size: 12px; font-weight: 600; text-transform: capitalize;
        }
        .sms-status-delivered { background: #d4edda; color: #155724; }
        .sms-status-sent { background: #cce5ff; color: #004085; }
        .sms-status-queued { background: #fff3cd; color: #856404; }
        .sms-status-undelivered { background: #ffe5d0; color: #8a4b08; }
        .sms-status-failed { background: #f8d7da; color: #721c24; }
        .sms-status-unsubscribed { background: #f8d7da; color: #721c24; }
        .sms-status-default { background: #e9ecef; color: #666; }

        .scd-overlay {
          display: none; position: fixed; inset: 0; background: rgba(0,0,0,0.5);
          z-index: 9999; justify-content: center; align-items: flex-start;
          padding-top: 60px; overflow-y: auto;
        }
        .scd-overlay.active { display: flex; }
        .scd-modal {
          background: #fff; border-radius: 10px; width: 90%; max-width: 620px;
          box-shadow: 0 8px 30px rgba(0,0,0,0.25); margin-bottom: 40px;
        }
        .scd-header {
          display: flex; justify-content: space-between; align-items: center;
          padding: 16px 20px; border-bottom: 1px solid #e5e5e5;
        }
        .scd-header h3 { margin: 0; font-size: 16px; font-weight: 600; }
        .scd-close {
          background: none; border: none; font-size: 22px; cursor: pointer;
          color: #999; line-height: 1; padding: 0 4px;
        }
        .scd-close:hover { color: #333; }
        .scd-body { padding: 20px; }
        .scd-loading { text-align: center; padding: 40px; color: #999; }

        .scd-contact-header {
          display: flex; justify-content: space-between; align-items: flex-start;
          margin-bottom: 20px;
        }
        .scd-contact-info { flex: 1; }
        .scd-contact-name { font-size: 18px; font-weight: 600; color: #333; margin: 0 0 4px; }
        .scd-contact-phone { color: #666; font-size: 14px; margin: 0; }
        .scd-contact-email { color: #999; font-size: 13px; margin: 2px 0 0; }
        .scd-patient-link {
          display: inline-flex; align-items: center; gap: 5px;
          color: #00aff0; text-decoration: none; font-size: 13px;
          padding: 6px 12px; border: 1px solid #ddd; border-radius: 5px;
          background: white; white-space: nowrap; flex-shrink: 0;
          transition: background 0.2s;
        }
        .scd-patient-link:hover { background: #f5f5f5; }
        .scd-sid {
          font-size: 11px; color: #aaa; margin-top: 6px; font-family: monospace;
          word-break: break-all;
        }

        .scd-section { margin-bottom: 20px; }
        .scd-section-title {
          font-size: 13px; font-weight: 600; color: #333;
          text-transform: uppercase; letter-spacing: 0.5px;
          margin-bottom: 12px; padding-bottom: 6px; border-bottom: 1px solid #e5e5e5;
        }

        .scd-timeline { display: flex; flex-direction: column; gap: 8px; }
        .scd-timeline-row { display: flex; gap: 8px; }
        .scd-timeline-row > .scd-timeline-item { flex: 1; }
        .scd-timeline-item {
          display: flex; align-items: center; gap: 10px;
          padding: 10px 14px; background: #f8f9fa; border-radius: 6px;
        }
        .scd-timeline-item.error { background: #fff5f5; }
        .scd-timeline-icon {
          width: 28px; height: 28px; border-radius: 50%;
          display: flex; align-items: center; justify-content: center;
          flex-shrink: 0;
        }
        .scd-timeline-label { font-weight: 600; font-size: 13px; color: #333; }
        .scd-timeline-date { font-size: 12px; color: #888; margin-left: auto; }
        .scd-timeline-date.na { color: #ccc; }

        .scd-error-box {
          background: #fff5f5; border: 1px solid #f8d7da; border-radius: 6px;
          padding: 12px 14px; margin-top: 8px;
        }
        .scd-error-label {
          font-size: 11px; font-weight: 600; text-transform: uppercase;
          color: #721c24; margin-bottom: 4px;
        }
        .scd-error-msg { font-size: 13px; color: #721c24; line-height: 1.5; word-break: break-word; }
        .scd-error-code { font-size: 11px; color: #999; margin-top: 4px; font-family: monospace; }

        .scd-events-table { width: 100%; border-collapse: collapse; font-size: 13px; }
        .scd-events-table th {
          text-align: left; padding: 8px 12px; font-size: 11px;
          font-weight: 600; text-transform: uppercase; color: #666;
          background: #f8f9fa; border-bottom: 1px solid #e5e5e5;
        }
        .scd-events-table td {
          padding: 10px 12px; border-bottom: 1px solid #f0f0f0; color: #333;
        }
        .scd-events-table tr:last-child td { border-bottom: none; }
        .scd-event-badge {
          display: inline-block; padding: 2px 8px; border-radius: 10px;
          font-size: 11px; font-weight: 600; text-transform: capitalize;
        }
        .scd-empty { text-align: center; color: #999; padding: 20px; font-size: 13px; }
      `}</style>

      <div className="flex flex-col h-full overflow-hidden p-6">
        {/* Header */}
        <div className="flex-shrink-0 space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Link to={`/marketing/sms-campaigns/${id}/stats`} className="p-1 rounded hover:bg-slate-100">
                <ArrowLeft className="h-5 w-5 text-slate-500" />
              </Link>
              <h1 className="text-xl font-semibold text-slate-800">SMS Campaign Contacts</h1>
              <div className="flex items-center gap-2 ml-2">
                <span className="text-2xl font-bold text-blue-500">{totalRecipients.toLocaleString()}</span>
                <span className="text-sm font-semibold text-slate-500">{totalRecipients === 1 ? 'recipient' : 'recipients'}</span>
              </div>
            </div>
          </div>

          {/* Status filter + search */}
          <div className="flex items-center gap-4">
            <FilterSelect
              options={FILTER_OPTIONS}
              value={contactFilter}
              onChange={(v) => { setContactFilter(v); setPage(1); }}
              label="Filter by status"
              className="w-60"
            />
            <ContactLeadFilter
              value={leadFilter}
              onChange={(v) => { setLeadFilter(v); setPage(1); }}
              total={totalRecipients}
              leadCount={statusCounts?.lead}
              recipientCount={statusCounts?.leadRecipient}
              referralCount={statusCounts?.leadReferral}
            />
            <div className="ml-auto">
              <Input
                value={search}
                onChange={e => { setSearch(e.target.value); setPage(1); }}
                placeholder="Search contacts..."
                className="w-64"
                leftAddon={<Search className="h-4 w-4" />}
              />
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="flex-1 min-h-0 mt-4">
          <DataTable<SmsCampaignContact>
            data={contacts}
            columns={columns}
            keyField="smsCampaignContactID"
            loading={isLoading}
            page={page}
            pageSize={perPage}
            total={paginationTotal}
            onPageChange={setPage}
            onRowClick={(row) => openContactDetail(row)}
            rowClassName={() => 'cursor-pointer hover:bg-slate-50'}
          />
        </div>
      </div>

      {/* Contact Detail Modal */}
      <div
        className={`scd-overlay${modalContact && !leadModal.isOpen ? ' active' : ''}`}
        onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}
      >
        <div className="scd-modal">
          <div className="scd-header">
            <h3>Contact Details</h3>
            <button className="scd-close" onClick={closeModal}>&times;</button>
          </div>
          <div className="scd-body">
            {detailLoading && (
              <div className="scd-loading"><Loader2 className="h-6 w-6 animate-spin inline-block" /> Loading...</div>
            )}
            {!detailLoading && detailData && <ContactDetailBody data={detailData} />}
          </div>
        </div>
      </div>

      {leadModal.modal}
    </ContactLeadOpener>
  );
}

function ContactDetailBody({ data }: { data: SmsContactDetailResponse }) {
  const userTz = useUserTimezone();
  const c = data.contact;
  const name = `${c.firstName || ''} ${c.lastName || ''}`.trim();
  const events = data.events;
  const leads = data.leads ?? [];

  return (
    <>
      {/* Contact header */}
      <div className="scd-contact-header">
        <div className="scd-contact-info">
          {name && <p className="scd-contact-name">{name}</p>}
          <p className="scd-contact-phone">{formatPhone(c.phoneNumber)}</p>
          {c.email && <p className="scd-contact-email">{c.email}</p>}
          {c.twilioMessageSid && (
            <p className="scd-sid">SID: {c.twilioMessageSid}</p>
          )}
        </div>
        {c.patientID && (
          <Link to={`/patients/${c.patientID}`} className="scd-patient-link">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
              <circle cx="12" cy="7" r="4" />
            </svg>
            View Patient
          </Link>
        )}
      </div>

      {/* Delivery Timeline */}
      <div className="scd-section">
        <div className="scd-section-title">Delivery Timeline</div>
        <div className="scd-timeline">
          <div className="scd-timeline-row">
            <TimelineItem status="queued" label="Queued" date={c.createDate} tz={userTz} />
            <TimelineItem status="sent" label="Sent" date={c.sentAt} tz={userTz} />
          </div>
          <div className="scd-timeline-row">
            <TimelineItem status="delivered" label="Delivered" date={c.deliveredAt} tz={userTz} />
            {(c.deliveryStatus === 'failed' || c.deliveryStatus === 'undelivered') && (
              <TimelineItem status={c.deliveryStatus} label={c.deliveryStatus === 'failed' ? 'Failed' : 'Undelivered'} date={c.sentAt} tz={userTz} isError />
            )}
          </div>
        </div>
        {/* A campaign is one message, so its leads sit under its delivery. */}
        <ContactLeadList leads={leads} tz={userTz} className="mt-2" />
      </div>

      {/* Error Details */}
      {(c.errorMessage || c.errorCode) && (
        <div className="scd-section">
          <div className="scd-section-title">Error Details</div>
          <div className="scd-error-box">
            {c.errorMessage && (
              <>
                <div className="scd-error-label">Message</div>
                <div className="scd-error-msg">{c.errorMessage}</div>
              </>
            )}
            {c.errorCode && (
              <div className="scd-error-code">Error Code: {c.errorCode}</div>
            )}
          </div>
        </div>
      )}

      {/* Event Log */}
      <div className="scd-section">
        <div className="scd-section-title">Event Log</div>
        {events.length > 0 ? (
          <table className="scd-events-table">
            <thead>
              <tr>
                <th>Event</th>
                <th>Date</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              {events.map((ev, i) => {
                const si = STATUS_ICON[ev.eventType] || STATUS_ICON.queued;
                return (
                  <tr key={i}>
                    <td>
                      <span
                        className="scd-event-badge"
                        style={{ background: si.bg, color: si.color }}
                      >
                        {ev.eventType}
                      </span>
                    </td>
                    <td style={{ fontSize: 12, color: '#888' }}>{fmtDate(ev.eventDate, userTz)}</td>
                    <td style={{ fontSize: 12, color: '#999' }}>
                      {ev.errorMessage || ev.errorCode || '\u2014'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <div className="scd-empty">
            No events recorded yet. Events will appear here once the status callback webhook receives delivery updates from Twilio.
          </div>
        )}
      </div>
    </>
  );
}

function TimelineItem({ status, label, date, tz, isError }: { status: string; label: string; date: string | null; tz: string; isError?: boolean }) {
  const si = STATUS_ICON[status] || STATUS_ICON.queued;
  return (
    <div className={`scd-timeline-item${isError ? ' error' : ''}`}>
      <div className="scd-timeline-icon" style={{ background: si.bg, color: si.color }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" dangerouslySetInnerHTML={{ __html: si.svg }} />
      </div>
      <span className="scd-timeline-label">{label}</span>
      <span className={`scd-timeline-date${date ? '' : ' na'}`}>{date ? fmtDate(date, tz) : '\u2014'}</span>
    </div>
  );
}
